import React, { useState, useEffect, useCallback } from 'react';
import './AuthUsersList.css';
import { apiFetch } from '../../../services/dashboardApi';
import DashboardButton from './ui/DashboardButton';
import NuevoUsuario from '../NuevoUsuario';

function AuthUsersList({ token, isAdmin }) {
    const [usuarios, setUsuarios] = useState([]);
    const [roles, setRoles] = useState([]);
    const [rolesPorUsuario, setRolesPorUsuario] = useState({});
    const [loading, setLoading] = useState(true);
    const [showRolForm, setShowRolForm] = useState(false);
    const [selectedUsuario, setSelectedUsuario] = useState(null);
    const [showNuevoUsuarioForm, setShowNuevoUsuarioForm] = useState(false);

    const fetchUsuarios = useCallback(async () => {
        try {
            setLoading(true);
            const response = await apiFetch('http://localhost:8000/api/auth/users/', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setUsuarios(data.results || data || []);
        } catch (err) {
            console.error('Error al cargar usuarios:', err);
        } finally {
            setLoading(false);
        }
    }, [token]);

    const fetchRoles = useCallback(async () => {
        try {
            const response = await apiFetch('http://localhost:8000/api/roles/', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setRoles(data.results || data || []);
        } catch (err) {
            console.error('Error al cargar roles:', err);
        }
    }, [token]);

    const fetchRolesAsignados = useCallback(async () => {
        try {
            const response = await apiFetch('http://localhost:8000/api/auth-user-roles/', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            const lista = data.results || data || [];
            const mapa = {};
            lista.forEach((item) => {
                if (!mapa[item.user]) mapa[item.user] = [];
                mapa[item.user].push(item.rol_detalle?.nombre || item.rol);
            });
            setRolesPorUsuario(mapa);
        } catch (err) {
            console.error('Error al cargar roles asignados:', err);
        }
    }, [token]);

    useEffect(() => {
        fetchUsuarios();
        fetchRoles();
        fetchRolesAsignados();
    }, [fetchUsuarios, fetchRoles, fetchRolesAsignados]);

    const handleToggleStaff = async (usuario) => {
        const accion = usuario.is_staff ? 'quitar' : 'marcar';
        const confirmMessage = usuario.is_staff
            ? `¿Quitar a ${usuario.username} como Staff?`
            : `¿Marcar a ${usuario.username} como Staff?`;

        if (!window.confirm(confirmMessage)) return;

        try {
            const response = await apiFetch(`http://localhost:8000/api/auth/users/${usuario.id}/toggle-staff/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                alert(`Usuario ${accion === 'marcar' ? 'marcado' : 'removido'} como Staff exitosamente`);
                fetchUsuarios();
            } else {
                const data = await response.json();
                alert('Error: ' + JSON.stringify(data));
            }
        } catch (err) {
            alert('Error: ' + err.message);
        }
    };

    const handleAsignarRol = (usuario) => {
        setSelectedUsuario(usuario);
        setShowRolForm(true);
    };

    const handleCrearNuevoUsuario = () => {
        setShowRolForm(false);
        setSelectedUsuario(null);
        setShowNuevoUsuarioForm(true);
    };

    if (!isAdmin) {
        return <p className="access-restricted">Acceso restringido: solo administradores pueden gestionar usuarios.</p>;
    }

    if (showRolForm && selectedUsuario) {
        return (
            <AsignarRolForm
                usuario={selectedUsuario}
                roles={roles}
                token={token}
                onCancel={() => {
                    setShowRolForm(false);
                    setSelectedUsuario(null);
                    fetchUsuarios();
                    fetchRolesAsignados();
                }}
            />
        );
    }

    if (showNuevoUsuarioForm) {
        return (
            <NuevoUsuario
                onUsuarioCreado={() => {
                    setShowNuevoUsuarioForm(false);
                    fetchUsuarios();
                    fetchRolesAsignados();
                }}
                onCancelar={() => {
                    setShowNuevoUsuarioForm(false);
                    fetchUsuarios();
                    fetchRolesAsignados();
                }}
            />
        );
    }

    return (
        <div>
            <h3>Gestión de Usuarios (auth_user)</h3>
            <div className="auth-users-header-actions">
                <DashboardButton variant="success" onClick={handleCrearNuevoUsuario}>
                    Crear Nuevo Usuario
                </DashboardButton>
            </div>

            {loading && <p>Cargando usuarios...</p>}

            {!loading && usuarios.length === 0 && <p>No hay usuarios registrados.</p>}

            {!loading && usuarios.length > 0 && (
                <table className="users-table">
                    <thead>
                        <tr className="table-head-row">
                            <th>Username</th>
                            <th>Email</th>
                            <th>Nombre</th>
                            <th>Rol</th>
                            <th>Staff</th>
                            <th>Activo</th>
                            <th>Superuser</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {usuarios.map(usuario => (
                            <tr key={usuario.id}>
                                <td>{usuario.username}</td>
                                <td>{usuario.email}</td>
                                <td>
                                    {usuario.first_name} {usuario.last_name}
                                </td>
                                <td>
                                    {rolesPorUsuario[usuario.id]?.join(', ') || usuario.rol_principal || 'Sin Rol'}
                                </td>
                                <td>
                                    {usuario.is_staff ? (
                                        <span className="status-yes">Sí</span>
                                    ) : (
                                        <span className="status-no">No</span>
                                    )}
                                </td>
                                <td>{usuario.is_active ? 'Sí' : 'No'}</td>
                                <td>{usuario.is_superuser ? (<span className="status-super">Sí</span>) : 'No'}</td>
                                <td>
                                    <DashboardButton variant="warning" onClick={() => handleAsignarRol(usuario)}>
                                        Cambiar Rol
                                    </DashboardButton>
                                    <DashboardButton
                                        variant={usuario.is_staff ? 'danger' : 'success'}
                                        onClick={() => handleToggleStaff(usuario)}
                                    >
                                        {usuario.is_staff ? 'Quitar Staff' : 'Marcar como Staff'}
                                    </DashboardButton>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

function AsignarRolForm({ usuario, roles, token, onCancel }) {
    const [selectedRol, setSelectedRol] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [rolesAsignados, setRolesAsignados] = useState([]);

    useEffect(() => {
        const fetchRolesAsignados = async () => {
            try {
                const response = await apiFetch(`http://localhost:8000/api/auth-user-roles/?user=${usuario.id}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const data = await response.json();
                setRolesAsignados(data.results || data || []);
            } catch (err) {
                setRolesAsignados([]);
            }
        };

        fetchRolesAsignados();
    }, [usuario, token]);

    const eliminarRolesExistentes = async () => {
        for (const asignado of rolesAsignados) {
            await apiFetch(`http://localhost:8000/api/auth-user-roles/${asignado.id}/`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Buscar el nombre del rol seleccionado
        const rolSeleccionado = roles.find(r => r.id.toString() === selectedRol.toString());
        if (!rolSeleccionado) {
            setMessage('Error: Rol no encontrado');
            return;
        }

        // Determinar el rol actual
        const rolActual = rolesAsignados.length > 0
            ? rolesAsignados[0].rol_detalle?.nombre || 'Rol actual'
            : 'Sin rol';

        // Mostrar confirmación
        const confirmMessage = rolesAsignados.length > 0
            ? `¿Está seguro de cambiar el rol de ${usuario.username} de "${rolActual}" a "${rolSeleccionado.nombre}"?`
            : `¿Está seguro de asignar el rol "${rolSeleccionado.nombre}" a ${usuario.username}?`;

        if (!window.confirm(confirmMessage)) {
            return;
        }

        setLoading(true);
        setMessage('');

        try {
            // Eliminar cualquier rol previo para garantizar un único rol por usuario
            await eliminarRolesExistentes();

            // Asignar nuevo rol directamente a auth_user
            const response = await apiFetch('http://localhost:8000/api/auth-user-roles/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    user: usuario.id,
                    rol: selectedRol
                })
            });

            if (response.ok) {
                setMessage('Rol asignado exitosamente');
                setTimeout(() => onCancel(), 1500);
            } else {
                const data = await response.json();
                setMessage('Error: ' + JSON.stringify(data));
            }
        } catch (err) {
            setMessage('Error: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <h3>Asignar Rol a Usuario</h3>
            <p><strong>Usuario:</strong> {usuario.username} ({usuario.email})</p>
            <p><strong>Rol actual:</strong> {usuario.rol_principal || 'Sin Rol'}</p>

            <form onSubmit={handleSubmit} className="form-max">
                <div className="mb-15">
                    <label>Seleccionar Rol:*</label>
                    <select
                        value={selectedRol}
                        onChange={(e) => setSelectedRol(e.target.value)}
                        required
                        className="select-full"
                    >
                        <option value="">-- Seleccionar Rol --</option>
                        {roles.map(rol => (
                            <option key={rol.id} value={rol.id}>
                                {rol.nombre} - {rol.descripcion}
                            </option>
                        ))}
                    </select>
                </div>

                {rolesAsignados.length > 0 && (
                    <div className="panel-muted mb-15">
                        <strong>Roles actuales:</strong>
                        <ul>
                            {rolesAsignados.map((asignado) => (
                                <li key={asignado.id}>{asignado.rol_detalle?.nombre || 'Rol'}</li>
                            ))}
                        </ul>
                        <DashboardButton
                            type="button"
                            onClick={async () => {
                                const rolActual = rolesAsignados[0]?.rol_detalle?.nombre || 'el rol actual';
                                const confirmMessage = `¿Está seguro de eliminar el rol "${rolActual}" de ${usuario.username}? El usuario quedará sin rol asignado.`;

                                if (!window.confirm(confirmMessage)) {
                                    return;
                                }

                                setLoading(true);
                                setMessage('');
                                try {
                                    await eliminarRolesExistentes();
                                    setMessage('Rol eliminado correctamente.');
                                    setRolesAsignados([]);
                                } catch (err) {
                                    setMessage('Error al eliminar rol.');
                                } finally {
                                    setLoading(false);
                                }
                            }}
                            variant="danger"
                            className="mt-10"
                        >
                            Eliminar rol actual
                        </DashboardButton>
                    </div>
                )}

                    {message && (
                        <p className={`message ${message.includes('Error') ? 'error' : 'success'}`}>
                            {message}
                        </p>
                    )}

                <div>
                        <DashboardButton type="submit" variant="success" className="mr-10" disabled={loading}>
                            {loading ? 'Asignando...' : 'Asignar Rol'}
                        </DashboardButton>
                        <DashboardButton type="button" variant="secondary" onClick={onCancel}>
                            Cancelar
                        </DashboardButton>
                </div>
            </form>
        </div>
    );
}

export default AuthUsersList;
